import { memo, useCallback } from 'react';

import { WorkspaceAgentPanelConsentDock } from '~/components/v2/components/workspace/workspace-agent-panel/components/consent/WorkspaceAgentPanelConsentDock';
import { PrivateWorkspaceAgentMonikerText } from '~/components/v2/components/workspace/workspace-agent-panel/composer-host/read/PrivateWorkspaceAgentMonikerText';
import { useWorkspaceAgentElicitationTracking } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useWorkspaceAgentElicitationTracking';
import { usePrivateWorkspaceAgentPanelSession } from '~/components/v2/components/workspace/workspace-agent-panel/PrivateWorkspaceAgentPanelProvider';
import { type BinaryChoiceElicitation } from '~/components/v2/components/workspace/workspace-agent-panel/schemas/elicitation';

export type PrivateWorkspaceAgentPanelBinaryChoiceDockProps = {
  elicitation: BinaryChoiceElicitation;
};

export const PrivateWorkspaceAgentPanelBinaryChoiceDock = memo(
  ({ elicitation }: PrivateWorkspaceAgentPanelBinaryChoiceDockProps) => {
    const session = usePrivateWorkspaceAgentPanelSession();
    const [primaryLabel, declineLabel] = elicitation.payload.options;
    const trackChoice = useWorkspaceAgentElicitationTracking({
      createdAt: elicitation.createdAt,
      elicitationId: elicitation.id,
      elicitationType: 'binary_choice',
      optionCount: elicitation.payload.options.length,
    });

    const handlePrimary = useCallback(() => {
      trackChoice({ cta: 'approve' });
      void session.respondToElicitation({
        elicitationId: elicitation.id,
        response: 'approved',
      });
    }, [elicitation.id, session, trackChoice]);

    const handleDecline = useCallback(() => {
      trackChoice({ cta: 'cancel' });
      void session.respondToElicitation({
        elicitationId: elicitation.id,
        response: 'denied',
      });
    }, [elicitation.id, session, trackChoice]);

    return (
      <WorkspaceAgentPanelConsentDock
        title={<PrivateWorkspaceAgentMonikerText value={elicitation.payload.prompt} />}
        approveLabel={primaryLabel}
        cancelLabel={declineLabel}
        data-testid="WORKSPACE_AGENT_BINARY_CHOICE"
        onApprove={handlePrimary}
        onCancel={handleDecline}
      />
    );
  },
);

PrivateWorkspaceAgentPanelBinaryChoiceDock.displayName = 'PrivateWorkspaceAgentPanelBinaryChoiceDock';
